// Copyright 2016 Sorama B.V.

import { autoinject, bindable, computedFrom, customElement, Disposable } from "aurelia-framework";
import { Rect } from "controls/plot/IPlotPart";
import { Line } from "controls/plot/parts/LinearAxisPart";
import { Point3D, Point3f } from "datatypes/SoramaLocalApi";
import { Point3DExtension } from "extensions/Point3DExtension";
import { URA, URAProperties } from "libraries/Math/URA";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { Color } from "services/Color/Color";

/**
 * Given microphone positions, an optional channelselection, and optional microphone values, this component will draw the set of microphones
 * Can be used to select microphones by clicking, if the canModify property is set to true.
 */
@customElement("channel-grid")
@autoinject
export class ChannelGrid {
    private static readonly defaultMicSize: number = 0.009; // In meters (microphone space)
    private static readonly padding: number = 0.02; // In meters (microphone space)

    private static readonly selectedColor: Color = Color.fromHex("f5a053"); // $color-sorama-orange
    private static readonly linesColor: Color = Color.fromHex("252d36"); // $color-sorama-black
    private static readonly value0Color: Color = Color.fromHex("252d36"); // $color-sorama-black
    private static readonly value1Color: Color = Color.fromHex("fbfbfb"); // $color-sorama-white
    private static readonly disabledMicColor: Color = Color.fromHex("34495e"); // $color-sorama-darkblue

    /**
     * Uniform rectangular array properties, is recalculated any time the microphonePositions changes
     * Helps determine the spacing of microphones when it is in a regular grid
     */
    private uraProperties: URAProperties;

    /** The entire component reference, to use for full screen mode */
    protected containerElement: HTMLElement;

    /** Canvas to draw the microphones on */
    protected canvas: HTMLCanvasElement;

    /** Makes sure that any changes to the selection outside this component is reflected in the view */
    private channelSelectionObserver: Disposable = null;

    /* Makes sure we redraw when the size of the canvas changes */
    private resizeObserver: ResizeObserver;

    /** The index of the microphone that is currently hovered over */
    private microphoneHoverIndex = -1;

    //#region Bindable properties

    /**
     * Whether the user can modify the channel selection or just view it
     */
    @bindable
    public canModify = false;

    /**
     * A channel selection object that keeps the selection state
     */
    @bindable
    public channelSelection: ChannelSelection = null;

    /**
     * The positions of the microphones as Point3D {x, y, z} (NOT Point3f {X, Y, Z})
     */
    @bindable
    public microphonePositions: Point3D[] = null;

    /**
     * Any number value for each of the microphones, e.g. the sensitivity of the microphone
     */
    @bindable
    public microphoneValues: number[] = null;

    @bindable()
    public mutedMicrophones?: Point3D[] = null;

    //#endregion

    @computedFrom("uraProperties", "uraProperties.isUraSubset")
    protected get isUraSubset(): boolean {
        return this.uraProperties?.isUraSubset ?? false;
    }

    // If it is URA, but not a multiple of 64 mics
    @computedFrom("isUraSubset", "microphonePositions")
    protected get isStrictUraSubset(): boolean {
        return this.isUraSubset && this.microphonePositions.length % 64 != 0;
    }

    protected get showFullScreenButton(): boolean {
        return this.microphonePositions?.length > 64;
    }

    protected get isFullScreen(): boolean {
        return document.fullscreenElement === this.containerElement;
    }

    //#region Lifecycle
    public bind(): void {
        this.microphonePositionsChanged();

        // Create resize observer
        this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            window.requestAnimationFrame((): void | undefined => {
                if (!Array.isArray(entries) || !entries.length) {
                    return;
                }
                this.redraw();
            });
        });

        // Start observing the container element
        if (this.containerElement) {
            this.resizeObserver.observe(this.containerElement);
        }

        if (this.canModify) {
            this.canvas.addEventListener("mousemove", this.onMouseMove.bind(this));
            this.canvas.addEventListener("mouseleave", this.onMouseLeave.bind(this));
            this.canvas.addEventListener("mousedown", this.onMouseDown.bind(this));
        }
    }

    public unbind(): void {
        this.channelSelectionObserver?.dispose();

        // Clean up resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        if (this.canvas && this.canModify) {
            this.canvas.removeEventListener("mousemove", this.onMouseMove.bind(this));
            this.canvas.removeEventListener("mouseleave", this.onMouseLeave.bind(this));
            this.canvas.removeEventListener("mousedown", this.onMouseDown.bind(this));
        }
    }
    //#endregion

    //#region Property change callbacks (redraw if any of the inputs change)
    public microphoneValuesChanged() {
        this.redraw();
    }

    public channelSelectionChanged() {
        // Dispose of the old observer if it exists
        this.channelSelectionObserver?.dispose();

        // Subscribe to changes in the channel selection
        if (this.channelSelection) {
            this.channelSelectionObserver = this.channelSelection.changeObserver(() => {
                this.redraw();
            });
        }

        // trigger the change observer
        this.redraw();
    }

    public microphonePositionsChanged() {
        if (!this.microphonePositions) {
            return;
        }

        this.microphonePositions.forEach((mic) => {
            // Convert to lowercase because for some reason some apis return Point3D and some Point3f
            mic.x = (mic as unknown as Point3f).X ?? mic.x;
            mic.y = (mic as unknown as Point3f).Y ?? mic.y;
            mic.z = (mic as unknown as Point3f).Z ?? mic.z;
        });
        this.computeURAProperties();
        this.redraw();
    }

    //#endregion

    //#region Event handlers
    protected toggleFullScreen(event: Event): void {
        // prevent click from bubbling up
        event.stopPropagation();

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            this.containerElement.requestFullscreen();
        }

        this.redraw();
    }

    private onMouseMove(event: MouseEvent): void {
        const rect = this.canvas.getBoundingClientRect();
        const normX = (event.clientX - rect.left) / this.canvas.width;
        const normY = 1 - (event.clientY - rect.top) / this.canvas.height; // 1 - because we flipped the y axis when drawing

        const viewBox = this.computeMicrophonePositionBounds();
        const micX = normX * (viewBox.width + 2 * ChannelGrid.padding) + viewBox.x - ChannelGrid.padding;
        const micY = normY * (viewBox.height + 2 * ChannelGrid.padding) + viewBox.y - ChannelGrid.padding;

        // Find closest microphone
        let closestIndex = -1;
        let closestDistance = Number.MAX_VALUE;
        this.microphonePositions.forEach((mic, index) => {
            const distance = Math.hypot(mic.x - micX, mic.y - micY);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });

        // Only update if it actually changed
        if (closestIndex != this.microphoneHoverIndex) {
            this.microphoneHoverIndex = closestIndex;
            // show microphone index as tooltip by setting the title attribute
            this.canvas.title = `Microphone #${closestIndex + 1}`;
            this.redraw();
        }
    }

    private onMouseLeave(): void {
        this.microphoneHoverIndex = -1;
        this.canvas.title = "";
        this.redraw();
    }

    private onMouseDown(event: MouseEvent): void {
        if (this.microphoneHoverIndex === -1) {
            return;
        }
        if (this.isMuted(this.microphoneHoverIndex)) {
            return;
        }
        this.channelSelection.toggle(this.microphoneHoverIndex);
    }
    //#endregion

    private computeURAProperties(): void {
        if (this.microphonePositions) {
            // To speed up drawing CAM1K (in case of ChannelSelector), we can just give the URA properties right away
            if (this.microphonePositions.length === 1024) {
                const spacing = 0.02;
                this.uraProperties = new URAProperties(true, true, spacing * 4 * 8, spacing * 4 * 8, spacing);
            } else {
                this.uraProperties = URA.getUraProperties3D(this.microphonePositions);
            }
            // Get URA properties from a cloned array since the ura function orders the microphone positions
        } else {
            this.uraProperties = undefined;
        }
    }

    // The bounding box around the microphone positions
    private computeMicrophonePositionBounds(): Rect {
        const minX = Math.min(...(this.microphonePositions?.map((x) => x.x) ?? [0]));
        const maxX = Math.max(...(this.microphonePositions?.map((x) => x.x) ?? [1]));
        const minY = Math.min(...(this.microphonePositions?.map((x) => x.y) ?? [0]));
        const maxY = Math.max(...(this.microphonePositions?.map((x) => x.y) ?? [1]));

        const r = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        return new Rect(r.x, r.y, r.width, r.height);
    }

    private get micSize(): number {
        if (!this.microphonePositions || this.microphonePositions?.length <= 1) {
            return ChannelGrid.defaultMicSize;
        }

        if (this.isUraSubset) {
            // If the size of the component is small and it has 32 or more mics in a row, show them larger
            const micChainCount = Math.max(this.uraProperties.widthInElements, this.uraProperties.heightInElements);
            if ((micChainCount >= 32 && this.canvas.width < 300) || this.canvas.height < 300) {
                return this.uraProperties.spacing * 0.3;
            }
            return this.uraProperties.spacing * 0.25;
        } else {
            const bounds = this.computeMicrophonePositionBounds();
            const minSize = Math.min(bounds.width, bounds.height);
            const spacingEstimate = minSize / Math.sqrt(this.microphonePositions.length);
            return spacingEstimate * 0.25;
        }
    }

    /**
     * When it is a URA, make sure all mics on the same row and column are connected with a line
     * Note that we only draw the line in 8x8 blocks since all CAMs currently are made of CAM64 arrays
     */
    protected get microphoneURALines(): Line[] {
        if (!this.isUraSubset || this.isStrictUraSubset) return [];
        const bounds = this.computeMicrophonePositionBounds();
        const spacing = this.uraProperties.spacing;
        const countY = this.uraProperties.heightInElements;
        const countX = this.uraProperties.widthInElements;

        const lines: Line[] = [];

        const createLines = (count: number, isVertical: boolean) => {
            for (let i = 0; i < count; i++) {
                const primary = bounds[isVertical ? "x" : "y"] + i * spacing;

                // Draw 8 lines for each 8x8 block
                for (let j = 0; j < (isVertical ? countY : countX); j += 8) {
                    const secondary = bounds[isVertical ? "y" : "x"] + j * spacing;
                    lines.push({
                        x0: isVertical ? primary : secondary,
                        y0: isVertical ? secondary : primary,
                        x1: isVertical ? primary : secondary + 7 * spacing,
                        y1: isVertical ? secondary + 7 * spacing : primary,
                    });
                }
            }
        };

        createLines(countX, true); // Draw vertical lines
        createLines(countY, false); // Draw horizontal lines

        return lines;
    }

    /** When it is a setup of CAM64s, draw a square around each 8x8 microphones */
    protected get microphoneURARects(): Rect[] {
        if (!this.isUraSubset || this.isStrictUraSubset) return [];

        const bounds = this.computeMicrophonePositionBounds();
        const spacing = this.uraProperties.spacing;

        const countY = this.uraProperties.heightInElements;
        const countX = this.uraProperties.widthInElements;

        // If the number of microphones is not a multiple of 8, don't draw the rectangles
        if (countX % 8 !== 0 || countY % 8 !== 0) {
            return [];
        }

        const rects: Rect[] = [];

        for (let i = 0; i < countX; i += 8) {
            for (let j = 0; j < countY; j += 8) {
                const x = bounds.x + i * spacing;
                const y = bounds.y + j * spacing;
                rects.push(new Rect(x - spacing / 2, y - spacing / 2, 8 * spacing, 8 * spacing));
            }
        }

        return rects;
    }

    private redraw(): void {
        if (!this.microphonePositions) {
            return;
        }

        const viewBox = this.computeMicrophonePositionBounds();

        // Set canvas dimensions to match viewBox ratio while keeping width the same
        const ratio = (viewBox.height + ChannelGrid.padding * 2) / (viewBox.width + 2 * ChannelGrid.padding);
        this.canvas.width = this.canvas.clientWidth;
        this.canvas.height = this.canvas.clientWidth * ratio;
        this.canvas.style.height = `${this.canvas.height}px`;

        const ctx = this.canvas.getContext("2d");
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Go to normalized coordinate space
        ctx.scale(this.canvas.width, this.canvas.height);

        // Flip along vertical axis
        ctx.scale(1, -1);
        ctx.translate(0, -1);

        // Go to microphone space (in meters) with some extra padding
        ctx.scale(1 / (viewBox.width + ChannelGrid.padding * 2), 1 / (viewBox.height + ChannelGrid.padding * 2));
        ctx.translate(-viewBox.x + ChannelGrid.padding, -viewBox.y + ChannelGrid.padding);

        if (!this.isUraSubset) {
            ctx.strokeStyle = ChannelGrid.linesColor.hex;
            ctx.lineWidth = 0.005;
            const offset = 0.016;
            const cornerRadius = 0.08;
            ctx.roundRect(
                viewBox.x - offset,
                viewBox.y - offset,
                viewBox.width + offset * 2,
                viewBox.height + offset * 2,
                cornerRadius
            );
            ctx.stroke();
        }

        // Draw URA lines and rectangles if applicable
        if (this.isUraSubset) {
            ctx.strokeStyle = ChannelGrid.linesColor.hex;
            ctx.lineWidth = 0.003;

            // Draw URA lines
            this.microphoneURALines.forEach((line) => {
                ctx.line(line.x0, line.y0, line.x1, line.y1);
                ctx.stroke();
            });

            // Draw URA rectangles
            ctx.lineWidth = 0.005;
            this.microphoneURARects.forEach((rect) => {
                ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
            });
        }

        this.microphonePositions.forEach((mic, index) => {
            const isSelected = this.channelSelection?.selected(index) ?? false;
            const isHovered = this.microphoneHoverIndex === index;
            const isMuted = this.isMuted(index);
            let value = this.microphoneValues?.[index] ?? 0.7;

            ctx.circle(mic.x, mic.y, this.micSize);
            const valueColor = ChannelGrid.value0Color.mix(ChannelGrid.value1Color, value);

            let fillColor = valueColor;

            if (isMuted) {
                fillColor = ChannelGrid.disabledMicColor;
            }
            if (!this.canModify) {
                fillColor = ChannelGrid.disabledMicColor;
            }

            if (isSelected) {
                fillColor = ChannelGrid.selectedColor;
            }

            if (isHovered) {
                fillColor = fillColor.darken(0.3);
            }

            ctx.strokeStyle = ChannelGrid.linesColor.hex;
            ctx.lineWidth = 0.003;
            ctx.stroke();

            ctx.fillStyle = fillColor.hex;
            ctx.fill();
        });
    }
    private isMuted(index: number): boolean {
        if (!this.mutedMicrophones || !this.microphonePositions) {
            return false;
        }

        const mic = this.microphonePositions[index];
        return this.mutedMicrophones.some((m) => Point3DExtension.Equals(mic, m));
    }
}
